'use client';

/**
 * AdminProfileSections — role-gated admin/super-admin controls rendered INSIDE
 * the profile/avatar dropdown (UserDropdownMenu). Replaces the old standalone
 * Shield popover (admin-menu.jsx) which has been removed from the header.
 *
 * Two sections, each role-gated (read-only from the existing JWT/user object —
 * NO new auth logic):
 *   - "Admin"        (section_admin): Background animation, Chat, Nexus
 *                     Background Agent. Visible when is_admin OR
 *                     nexus_core_access.
 *   - "Super-Admin"  (section_super_admin): Theme preview. Visible when
 *                     nexus_core_access only.
 *
 * Active theme on load = the REAL org theme (from useOrganisationThemeContext),
 * so e.g. KAIE (org 49, base_theme='technic') shows "Technik (KAIE)" as active,
 * NOT a hard-coded 'default'.
 *
 * Theme selection:
 *   1. Swaps a dedicated <link id="xoffi-preview-theme"> appended at the END of
 *      <head>, so its :root{...} override wins the CSS cascade. The backend
 *      emits a COMPLETE explicit override for every NAME (incl. 'default'), so
 *      a preview always wins over the underlying per-org theme.
 *   2. Posts the canonical protocol message to ALL iframes so containers follow:
 *        postMessage({ type: 'xoffi:style-theme', theme: NAME }, '*')
 *      NAME ∈ {default,technic,modern,classic,minimal,corporate,creative}.
 *
 * Pure preview — never writes to the DB.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Layers,
  MessageCircleMore,
  Zap,
  Palette,
  Check,
  ChevronRight,
  Shield,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { BackendAuth } from '@/lib/auth-backend';
import { useTranslation } from '@/hooks/useTranslation';
import { useOrganisationThemeContext } from '@/providers/organisation-theme-provider';
import { Button } from '@/components/ui/button';
import { BackgroundPickerButton } from '@/components/ui/background-picker-button';
import { ChatSheet } from '@/partials/topbar/chat-sheet';
import { NexusHeaderIndicator } from '@/app/components/nexus/nexus-header-indicator';

const PREVIEW_LINK_ID = 'xoffi-preview-theme';

// Canonical protocol theme names (must match the backend ?theme=<name> set and
// the container postMessage contract).
const PREVIEW_THEMES = [
  { id: 'default', labelKey: 'admin_menu.theme.default' },
  { id: 'technic', labelKey: 'admin_menu.theme.technic' },
  { id: 'aqua', labelKey: 'admin_menu.theme.aqua' },
  { id: 'modern', labelKey: 'admin_menu.theme.modern' },
  { id: 'classic', labelKey: 'admin_menu.theme.classic' },
  { id: 'minimal', labelKey: 'admin_menu.theme.minimal' },
  { id: 'corporate', labelKey: 'admin_menu.theme.corporate' },
  { id: 'creative', labelKey: 'admin_menu.theme.creative' },
];
const VALID_THEME_IDS = new Set(PREVIEW_THEMES.map((t) => t.id));

/**
 * Post the canonical theme-style message to every iframe so containers mirror
 * the preview. Containers inject <link id="xoffi-preview-theme"
 * href="/api/theme/theme.css?theme=NAME"> as their LAST stylesheet.
 */
function postThemeToIframes(themeId) {
  if (typeof document === 'undefined') return;
  try {
    document.querySelectorAll('iframe').forEach((frame) => {
      try {
        frame.contentWindow?.postMessage(
          { type: 'xoffi:style-theme', theme: themeId },
          '*',
        );
      } catch {
        /* cross-origin iframe — ignore */
      }
    });
  } catch {
    /* no iframes / SSR — ignore */
  }
}

/**
 * Inject / replace the preview stylesheet <link> in the Core <head>, appended
 * LAST so its :root{} block wins the cascade. 'default' still injects an
 * explicit override (the backend emits the base palette for ?theme=default),
 * so previewing 'default' visibly resets over the org theme.
 */
function applyPreviewTheme(themeId) {
  if (typeof document === 'undefined') return;
  if (!themeId || !VALID_THEME_IDS.has(themeId)) return;

  const href = `/api/theme/theme.css?theme=${encodeURIComponent(themeId)}`;
  const existing = document.getElementById(PREVIEW_LINK_ID);
  if (existing) {
    existing.setAttribute('href', href);
  } else {
    const link = document.createElement('link');
    link.id = PREVIEW_LINK_ID;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }
}

/**
 * Swap the STRUCTURAL `theme-<name>` class on <html> to match the preview.
 * applyPreviewTheme only swaps the color tokens (<link>); the structural rules
 * in css/themes/<name>.css (e.g. truu's `.theme-aqua table` font-size, technic's
 * zebra) are keyed on this class. Without swapping it, previewing another theme
 * leaves the old structure in place (e.g. truu fonts persist on "Standard").
 * Mirrors hooks/useOrganisationTheme.js::applyThemeClass (remove-all-then-add).
 * 'default' has no `.theme-default` rules → structure reverts to platform base.
 */
function applyPreviewThemeClass(themeId) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // Remove every known theme class + a defensive sweep of any stray `theme-*`.
  PREVIEW_THEMES.forEach((t) => root.classList.remove(`theme-${t.id}`));
  Array.from(root.classList).forEach((c) => {
    if (c.startsWith('theme-')) root.classList.remove(c);
  });
  if (themeId) root.classList.add(`theme-${themeId}`);
}

const OPEN_STATE_PREFIX = 'xoffi:profile-menu:section-open:';

/**
 * Collapsible section inside the profile dropdown. Collapsed by DEFAULT: the
 * expanded Admin + Super-Admin blocks (3 rows + 8 theme chips) pushed the
 * logout button out of the viewport on short screens. The open/closed state is
 * remembered per section in localStorage, so a user who works with the theme
 * preview does not have to re-open it on every page load.
 *
 * The trigger is a plain <button>, NOT a DropdownMenuItem — a DropdownMenuItem
 * would close the whole dropdown on select. A click inside the content is not
 * an outside-click, so the menu stays open while toggling.
 */
function CollapsibleSection({ id, icon, label, children }) {
  const [open, setOpen] = useState(false);

  // Restore persisted state after mount (never during SSR → no hydration diff).
  useEffect(() => {
    try {
      setOpen(window.localStorage.getItem(`${OPEN_STATE_PREFIX}${id}`) === '1');
    } catch {
      /* localStorage blocked (private mode) — stay collapsed */
    }
  }, [id]);

  const handleOpenChange = useCallback(
    (next) => {
      setOpen(next);
      try {
        window.localStorage.setItem(`${OPEN_STATE_PREFIX}${id}`, next ? '1' : '0');
      } catch {
        /* ignore */
      }
    },
    [id],
  );

  return (
    <div className="border-t border-border px-2 py-1.5">
      <Collapsible open={open} onOpenChange={handleOpenChange}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <ChevronRight
              className={`size-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${
                open ? 'rotate-90' : ''
              }`}
            />
            {icon}
            <span className="grow text-xs font-medium text-muted-foreground">
              {label}
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-1">{children}</CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export function AdminProfileSections() {
  const { t } = useTranslation();
  // Plain-string variant for attribute contexts (aria-label / title), where the
  // editable React-element wrapper from t() would stringify to "[object Object]".
  const ts = useCallback((key, opts) => t(key, { ...opts, editable: false }), [t]);
  const [user, setUser] = useState(null);

  // Real org theme (used to mark the active preview chip on load).
  let orgTheme = null;
  try {
    orgTheme = useOrganisationThemeContext();
  } catch {
    // Rendered outside the provider (defensive) — fall back to no org theme.
    orgTheme = null;
  }
  const orgBaseTheme = useMemo(() => {
    const base = (orgTheme?.theme?.base_theme || '').toLowerCase();
    return VALID_THEME_IDS.has(base) ? base : 'default';
  }, [orgTheme]);

  const [activeTheme, setActiveTheme] = useState('default');

  // Initialise the active chip from the REAL org theme once it is known.
  useEffect(() => {
    setActiveTheme(orgBaseTheme);
  }, [orgBaseTheme]);

  // Read gating flags from the existing user object (set at login).
  useEffect(() => {
    setUser(BackendAuth.getUser());

    const handleStorage = (e) => {
      if (e.key === 'user_data') {
        try {
          setUser(e.newValue ? JSON.parse(e.newValue) : null);
        } catch {
          setUser(null);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const isAdmin = useMemo(() => {
    if (!user) return false;
    // Super-admin (nexus_core_access, e.g. master-password login) is strictly
    // above a plain admin → always gets admin-level access. Without this a
    // super-admin who lacks the is_admin flag would see nothing.
    if (user.nexus_core_access) return true;
    const raw = user.is_admin ?? user.isAdmin ?? user.role;
    if (raw === true) return true;
    if (typeof raw === 'number') return raw === 1;
    if (typeof raw === 'string') {
      const s = raw.toLowerCase();
      if (s === '1' || s === 'true' || s.includes('admin')) return true;
    }
    const role = (user?.role || '').toLowerCase();
    return role === 'admin' || role === 'super_admin';
  }, [user]);

  const isSuperAdmin = useMemo(() => !!user?.nexus_core_access, [user]);

  const selectTheme = useCallback((themeId) => {
    setActiveTheme(themeId);
    applyPreviewTheme(themeId);
    applyPreviewThemeClass(themeId);
    postThemeToIframes(themeId);
  }, []);

  // Non-admins: render nothing at all.
  if (!isAdmin) return null;

  return (
    <>
      {/* ─── Admin section (collapsed by default) ─── */}
      <CollapsibleSection
        id="admin"
        icon={<Shield className="size-4 shrink-0 text-muted-foreground" />}
        label={t('admin_menu.section_admin')}
      >
        {/* Background animation — self-contained picker (own popover) */}
        <div className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-accent">
          <span className="flex items-center gap-2 text-sm text-foreground">
            <Layers className="size-4 text-muted-foreground" />
            {t('admin_menu.background_animation')}
          </span>
          <BackgroundPickerButton />
        </div>

        {/* Chat (not yet official → admin-gated here) */}
        <div className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-accent">
          <span className="flex items-center gap-2 text-sm text-foreground">
            <MessageCircleMore className="size-4 text-muted-foreground" />
            {t('admin_menu.chat')}
          </span>
          <ChatSheet
            trigger={
              <Button
                variant="ghost"
                mode="icon"
                shape="circle"
                aria-label={ts('admin_menu.chat')}
                title={ts('admin_menu.open')}
                className="size-9 hover:bg-primary/10 hover:[&_svg]:text-primary"
              >
                <MessageCircleMore className="size-4.5!" />
              </Button>
            }
          />
        </div>

        {/* Nexus Background Agent — self-contained indicator */}
        <div className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-accent">
          <span className="flex items-center gap-2 text-sm text-foreground">
            <Zap className="size-4 text-muted-foreground" />
            {t('admin_menu.nexus_background_agent')}
          </span>
          <NexusHeaderIndicator />
        </div>
      </CollapsibleSection>

      {/* ─── Super-Admin section (theme preview, collapsed by default) ─── */}
      {isSuperAdmin && (
        <CollapsibleSection
          id="super-admin"
          icon={<Palette className="size-4 shrink-0 text-muted-foreground" />}
          label={t('admin_menu.section_super_admin')}
        >
          <p className="px-2 pb-1.5 text-[11px] leading-snug text-muted-foreground">
            {t('admin_menu.theme_preview_hint')}
          </p>

          <div className="grid grid-cols-2 gap-1.5">
            {PREVIEW_THEMES.map((theme) => {
              const isActive = activeTheme === theme.id;
              return (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => selectTheme(theme.id)}
                  aria-pressed={isActive}
                  className={`flex min-h-[44px] items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-sm transition-colors duration-150 ${
                    isActive
                      ? 'border-primary/40 bg-primary/10 text-primary'
                      : 'border-border text-foreground hover:bg-accent'
                  }`}
                >
                  <span className="truncate">{t(theme.labelKey)}</span>
                  {isActive && <Check className="size-3.5 shrink-0 text-primary" />}
                </button>
              );
            })}
          </div>
        </CollapsibleSection>
      )}
    </>
  );
}
